import React from "react";
import { ArrowUpRight, Trees, Heart } from "lucide-react";
import PageHero from "../components/PageHeader";
import {
  PAGE_HEROES,
  CONSERVATION_COPY,
  PARTNERS_4_THE_WATER,
  COMMUNITY_LINKS,
} from "../data/site";

function PartnerBadge({ name, note, url }) {
  return (
    <a
      href={url || "#"}
      target={url && url !== "#" ? "_blank" : undefined}
      rel="noreferrer noopener"
      className="surface-card rounded-md p-5 flex flex-col items-center text-center min-h-[140px] hover:border-[#b8895a] transition-colors group"
    >
      <div className="h-10 w-10 rounded-full bg-[#f3ecdc] flex items-center justify-center mb-3">
        <Trees size={18} className="text-[#1a3a5c]" />
      </div>
      <p className="font-serif text-base md:text-lg text-[#13263c] leading-tight">
        {name}
      </p>
      {note && (
        <p className="text-[0.62rem] uppercase tracking-[0.2em] text-[#737373] mt-1">
          ({note})
        </p>
      )}
    </a>
  );
}

function CommunityCard({ name, category }) {
  return (
    <div className="surface-card rounded-md py-5 px-4 flex flex-col items-center justify-center text-center min-h-[88px]">
      <p className="font-serif text-lg md:text-xl text-[#13263c] leading-tight uppercase tracking-[0.05em]">
        {name}
      </p>
      {category && (
        <p className="text-[0.6rem] uppercase tracking-[0.18em] text-[#9a9a9a] mt-1.5">
          {category}
        </p>
      )}
    </div>
  );
}

export default function Conservation() {
  const hero = PAGE_HEROES.conservation;
  return (
    <div data-testid="conservation-page" className="bg-[#f7f4ec]">
      <PageHero image={hero.image} title="Conservation & Community" testId="conservation-hero" />

      {/* Montana DNA — image left + copy right */}
      <section
        data-testid="montana-dna-section"
        className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
          <div className="md:col-span-6">
            <div className="img-frame aspect-[4/3] rounded-md">
              <img
                src="https://images.unsplash.com/photo-1579539771986-d715a566e4fb?auto=format&fit=crop&w=1600&q=80"
                alt="A Montana river valley"
              />
            </div>
          </div>
          <div className="md:col-span-6">
            <h2 className="font-serif text-4xl md:text-5xl text-[#13263c] leading-[1.05] tracking-tight">
              Montana DNA
            </h2>
            <hr className="accent-rule mt-4" />
            <p className="mt-6 text-[#2b2b2b] leading-relaxed text-lg max-w-xl">
              {CONSERVATION_COPY.montanaDna}
            </p>
            <p className="mt-5 text-[#4a4a4a] leading-relaxed max-w-xl">
              {CONSERVATION_COPY.montanaDnaSecondary}
            </p>
          </div>
        </div>
      </section>

      <hr className="hairline max-w-[1600px] mx-auto" />

      {/* 4 the Water */}
      <section
        data-testid="four-the-water"
        className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-4 flex md:flex-col md:items-start items-center gap-6">
            <div
              className="flex flex-col items-center justify-center h-28 w-28 md:h-32 md:w-32 rounded-md bg-[#f3ecdc]"
              aria-hidden="true"
            >
              <span className="font-serif text-5xl md:text-6xl text-[#3a5a2b] leading-none">4</span>
              <span className="text-[0.6rem] tracking-[0.2em] uppercase mt-1 text-[#3a5a2b]">the Water</span>
            </div>
            <div className="md:hidden">
              <h2 className="font-serif text-3xl text-[#13263c] leading-tight">4 the Water</h2>
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="hidden md:block font-serif text-4xl md:text-5xl text-[#13263c] leading-[1.05] tracking-tight">
              4 the Water
            </h2>
            <hr className="accent-rule mt-4 hidden md:block" />
            <p className="mt-6 text-[#2b2b2b] leading-relaxed text-lg max-w-2xl">
              {CONSERVATION_COPY.fourTheWater}
            </p>

            <p className="overline mt-8" data-testid="four-the-water-partners-label">Our Partners</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mt-5">
              {PARTNERS_4_THE_WATER.map((p, i) => (
                <PartnerBadge key={p.name + i} {...p} />
              ))}
            </div>
          </div>
        </div>
      </section>

      <hr className="hairline max-w-[1600px] mx-auto" />

      {/* 1% for the Planet */}
      <section
        data-testid="one-percent-section"
        className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
          <div className="md:col-span-4 flex md:flex-col items-center md:items-start gap-6">
            <div
              className="flex items-center justify-center h-28 w-28 md:h-32 md:w-32 rounded-full bg-[#13263c] text-[#f7f4ec]"
              aria-hidden="true"
            >
              <div className="text-center leading-none">
                <div className="font-serif text-4xl md:text-5xl">1%</div>
                <div className="text-[0.55rem] tracking-[0.22em] uppercase mt-1 text-[#b8895a]">
                  For the Planet
                </div>
              </div>
            </div>
            <div className="md:hidden">
              <h2 className="font-serif text-3xl text-[#13263c] leading-tight">1% for the Planet</h2>
            </div>
          </div>
          <div className="md:col-span-8">
            <h2 className="hidden md:block font-serif text-4xl md:text-5xl text-[#13263c] leading-[1.05] tracking-tight">
              1% for the Planet
            </h2>
            <hr className="accent-rule mt-4 hidden md:block" />
            <p className="mt-6 text-[#2b2b2b] leading-relaxed text-lg max-w-2xl">
              {CONSERVATION_COPY.onePercent}
            </p>
          </div>
        </div>
      </section>

      <hr className="hairline max-w-[1600px] mx-auto" />

      {/* Community Links */}
      <section
        data-testid="community-links"
        className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-4">
            <h2 className="font-serif text-4xl md:text-5xl text-[#13263c] leading-[1.05] tracking-tight">
              Community Links
            </h2>
            <hr className="accent-rule mt-4" />
            <p className="mt-6 text-[#4a4a4a] leading-relaxed max-w-sm">
              {CONSERVATION_COPY.communityIntro}
            </p>
          </div>
          <div className="md:col-span-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
              {COMMUNITY_LINKS.map((c, i) => (
                <CommunityCard key={c.name + i} name={c.name} category={c.category} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Casting for Recovery */}
      <section
        data-testid="cfr-section"
        className="bg-[#13263c] text-[#f7f4ec]"
      >
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-14 py-16 md:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
          <div className="md:col-span-4">
            <div className="img-frame aspect-[4/5] rounded-md">
              <img
                src="https://images.unsplash.com/photo-1499242611767-cf8b9be02854?auto=format&fit=crop&w=1200&q=80"
                alt="Casting for Recovery"
              />
            </div>
          </div>
          <div className="md:col-span-6">
            <p className="overline text-[#b8895a]">Casting for Recovery</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#f7f4ec] mt-3 leading-[1.05] tracking-tight">
              {CONSERVATION_COPY.castingForRecoveryHeadline}
            </h2>
            <hr className="accent-rule mt-4" />
            <p className="mt-6 text-[#d6d0c4] leading-relaxed text-lg max-w-2xl">
              {CONSERVATION_COPY.castingForRecovery}
            </p>
          </div>
          <div className="md:col-span-2">
            <div className="flex flex-col items-center md:items-end gap-2">
              <div className="bg-[#f7f4ec] text-[#b8895a] font-serif px-5 py-4 rounded-md text-center">
                <p className="text-xs uppercase tracking-[0.2em] text-[#737373]">Casting</p>
                <p className="font-serif text-xl leading-none">for</p>
                <p className="font-serif text-2xl leading-none">Recovery</p>
              </div>
              <p className="text-xs uppercase tracking-[0.18em] text-[#b8895a]">Southwest Montana</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
